import config from '@config';
import { HTTPClient } from '@platform2-lib/httpz';
import { getToken } from '@platform2-lib/token';
import { captureException } from '@sentry/client';
import { useProfileStore } from '@store/profile';
import { decodeJwt, log } from '@utils';
import Bowser from 'bowser';
import { Analytics } from '../types/analytics.ts';

const client = new HTTPClient(
  config.env,
  `${config.api.origin}/${config.api.version}/analytics`
);

const track = async (
  eventName: Analytics.EventName,
  props?: Analytics.EventProps.AnyCompatible,
  shouldLogData = true
) => {
  const { user } = useProfileStore.getState();
  const bowserParser = Bowser.getParser(window.navigator.userAgent);
  const browser = bowserParser.getBrowser();
  const deviceType = bowserParser.getPlatformType(true);
  const token = getToken(config.env);
  const decodedToken = decodeJwt(token);

  const payload = {
    ['app_name']: `octopus-${config.env}`,
    ['event_name']: eventName,
    timestamp: Date.now().toString(),
    ['user_id']: decodedToken.payload.id,
    ['user_email']: user.email,
    properties: {
      ['browser_name']: browser.name,
      ['browser_version']: browser.version,
      ['device_type']: deviceType,
      ['org_id']: decodedToken.payload.oid,
      ['origin']: `octopus_${config.env}`,
      ['session_id']: `${decodedToken.payload.id}_${decodedToken.payload.iat}`,
      ['plan_handle_snapshot']: user['plan_handle'],
      ['user_agent']: window.navigator.userAgent,
      ...(props || {})
    },
    traits: {}
  };

  try {
    const response = await client.post('/track', payload);
    log(`Tracked event '${eventName.toLowerCase()}'.`);

    if (shouldLogData && props) {
      const formattedProps = Object.entries(props).reduce(
        (acc, [key, value]) => {
          if (Array.isArray(value)) {
            acc[key] = value.join(',');
          } else {
            acc[key] = value;
          }

          return acc;
        },
        {}
      );

      console.table(formattedProps);
    }

    return response;
  } catch (error: unknown) {
    captureException(error);
    console.warn(`[Failure] Sending analytic event failed on server's side.`);
  }
};

export const analytics = {
  completedReasoning: (props: Analytics.EventProps.CompletedReasoning) =>
    track(Analytics.EventName.CompletedReasoning, props),

  changedPromptExampleCategory: () =>
    track(Analytics.EventName.ChangedPromptExampleCategory),

  changedTitle: (props: Analytics.EventProps.ChangedTitle) =>
    track(Analytics.EventName.ChangedTitle, props),

  copiedChatId: (props: Analytics.EventProps.CopiedChatId) =>
    track(Analytics.EventName.CopiedChatId, props),

  deselectedAudience: (props: Analytics.EventProps.DeselectedAudience) =>
    track(Analytics.EventName.DeselectedAudience, props),

  openedApp: () => track(Analytics.EventName.OpenedApp),

  openedAudienceMenu: (props: Analytics.EventProps.OpenedAudienceMenu) =>
    track(Analytics.EventName.OpenedAudienceMenu, props),

  openedRecentChat: (props: Analytics.EventProps.OpenedRecentChat) =>
    track(Analytics.EventName.OpenedRecentChat, props),

  openedSources: (props: Analytics.EventProps.OpenedSources) =>
    track(Analytics.EventName.OpenedSources, props),

  selectedPromptExample: (props: Analytics.EventProps.SelectedPromptExample) =>
    track(Analytics.EventName.SelectedPromptExample, props),

  sentMessage: (props: Analytics.EventProps.SentMessage) =>
    track(Analytics.EventName.SentMessage, props),

  searchedAudience: (props: Analytics.EventProps.SearchedAudience) =>
    track(Analytics.EventName.SearchAudience, props),

  selectedAudience: (props: Analytics.EventProps.SelectedAudience) =>
    track(Analytics.EventName.SelectedAudience, props),

  initiatedDatasetChange: (
    props: Analytics.EventProps.InitiatedDatasetChange
  ) => track(Analytics.EventName.InitiatedDatasetChange, props),

  selectedDataset: (props: Analytics.EventProps.SelectedDataset) =>
    track(Analytics.EventName.SelectedDataset, props),

  exportedInsightAsImage: (
    props: Analytics.EventProps.ExportedInsightAsImage
  ) => track(Analytics.EventName.ExportedInsightAsImage, props),

  selectedChartMetric: (props: Analytics.EventProps.SelectedChartMetric) =>
    track(Analytics.EventName.SelectedChartMetric, props),

  viewedReasoningOverview: (
    props: Analytics.EventProps.ViewedReasoningOverview
  ) => track(Analytics.EventName.ViewedReasoningOverview, props),

  viewedSource: (props: Analytics.EventProps.ViewedSource) =>
    track(Analytics.EventName.ViewedSource, props),

  viewedSourceInfoDialog: () =>
    track(Analytics.EventName.ViewedSourceInfoDialog),

  viewedMoreThinkingOverviewItems: () =>
    track(Analytics.EventName.ViewedMoreThinkingOverviewItems),

  reportedPositiveFeedback: (
    props: Analytics.EventProps.ReportedPositiveFeedback
  ) => track(Analytics.EventName.ReportedPositiveFeedback, props),

  selectedNegativeFeedback: (
    props: Analytics.EventProps.SelectedNegativeFeedback
  ) => track(Analytics.EventName.SelectedNegativeFeedback, props),

  reportedNegativeFeedback: (
    props: Analytics.EventProps.ReportedNegativeFeedback
  ) => track(Analytics.EventName.ReportedNegativeFeedback, props),

  reportedNegativeFeedbackDetails: (
    props: Analytics.EventProps.ReportedNegativeFeedbackDetails
  ) => track(Analytics.EventName.ReportedNegativeFeedbackDetails, props),

  undidNegativeFeedback: (props: Analytics.EventProps.UndidNegativeFeedback) =>
    track(Analytics.EventName.UndidNegativeFeedback, props),

  viewedMoreNegativeFeedbackReasons: (
    props: Analytics.EventProps.ViewedMoreNegativeFeedbackReasons
  ) => track(Analytics.EventName.ViewedMoreNegativeFeedbackReasons, props)
};
